/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-extra-data.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import { useTranslation, useBoolean, useAuthorization, useSysAdminCanEditStudy } from 'hooks';
import { GET_EXTRA_FIELDS } from 'queries/central-monitoring';
import { StudyFields } from 'queries/generated/fragments.generated';
import NotificationService from 'utils/notification';
import AddField from 'views/components/admin/add-field';
import ExtraDataFieldsGrid from 'views/components/admin/extra-data-fields-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import Constants from '@utils/constants';
import { extraDataFields_OpraQueries_extraDataFields_ExtraDataField } from '@queries/generated/central-monitoring.generated';

const AdminStudyExtraData = ({ study }: { study: StudyFields }) => {
    const { t } = useTranslation();
    const [loadExtraFields, { loading, error, data }] = useLazyQuery(GET_EXTRA_FIELDS);
    const [currentField, setCurrentField] = useState(null);
    const user = useAuthorization();
    const canEdit = user.canWriteSystemAdminLevel(false, false, false, true);
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const sysAdminCanEditStudy = useSysAdminCanEditStudy();
    const addFieldOpen = useBoolean(false);

    useEffect(() => {
        if (study?.id)
            loadExtraFields({
                variables: {
                    studyId: study.id,
                },
            });
    }, [study]);

    const actions = useCallback(
        (item: extraDataFields_OpraQueries_extraDataFields_ExtraDataField) => [
            {
                label: 'admin:editField',
                icon: ['fal', 'edit'],
                onClick: () => {
                    setCurrentField(item);
                    addFieldOpen.setTrue();
                },
                enabled: canEdit || sysAdminCanEditStudy,
            },
        ],
        [],
    );

    if (!study) return <h3>{t('common:noStudySelected')}</h3>;

    if (!study || loading) return <Loading float />;

    if (error) return <Error error={error} />;

    return (
        <>
            <AddField
                open={addFieldOpen.value}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add' || action === 'edit')
                            loadExtraFields({
                                variables: {
                                    studyId: study.id,
                                },
                            });
                        if (action === 'add') NotificationService.showNotification(t('admin:fieldSuccessfulCreated'));
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:fieldSuccessfulUpdated', { id: currentField?.recordId }),
                            );
                        setCurrentField(null);
                        addFieldOpen.setFalse();
                    }
                }}
                currentField={currentField}
                fields={data?.extraDataFields ?? []}
                studyId={study?.id}
                isSysAdmin={isSysAdmin}
                isCM2Study={study.useLatestCentralMonitoring}
            />
            <PageTitle text={t('admin:manageExtraDataFields')} />
            <br />
            <Button
                id="add-field-btn"
                className="add-cv-btn main-button"
                disabled={!canEdit}
                onClick={() => {
                    addFieldOpen.setTrue();
                }}
                style={{ display: 'flex', marginLeft: 'auto', marginBottom: 30 }}
            >
                {t('admin:addField')}
            </Button>
            <ExtraDataFieldsGrid items={data?.extraDataFields ?? []} actions={actions} />
        </>
    );
};

export default AdminStudyExtraData;
